import { Box, Typography } from "@mui/material";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useGetRecentConfidenceScoresQuery } from "../../../useQuery";

const ConfidenceScoreGraph = ({ currentEditItem }) => {
  const testStepId = currentEditItem?.Test_Step;

  const { data, isLoading, isError } = useGetRecentConfidenceScoresQuery(testStepId, {
    enabled: testStepId != null,
  });

  if (isLoading) {
    return <Box p={3}>Loading confidence score history...</Box>;
  }

  if (isError) {
    return <Box p={3}>Unable to load confidence score history.</Box>;
  }


  // API returns runs oldest-first, so we use the order as-is (latest run is the last item)
  const chartData = (data?.data || []).map((entry) => ({
    testStepResultId: entry.testStepResultId,
    confidence_score: entry.confidence_score,
  }));

  if (chartData.length === 0) {
    return <Box p={3}>No confidence score history available for this step.</Box>;
  }

  const scores = chartData.map((d) => d.confidence_score);
  const latest = scores[scores.length - 1];
  const peak = data?.peak?.confidence_score ?? 0;

  return (
    <Box
      sx={{
        width: "100%",
        p: 3,
        bgcolor: "#fff",
        borderRadius: "8px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.06)",
      }}
    >
      {/* Header with summary stats */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        rowGap={2}
        mb={3}
      >
        <Box>
          <Typography fontWeight={600} fontSize="16px" color="#2B3539">
            Confidence Score Trend
          </Typography>
          <Typography fontSize="13px" color="#8089A8">
            Last {chartData.length} runs for this step
          </Typography>
        </Box>
        <Box display="flex" columnGap={3}>
          <Box textAlign="center">
            <Typography fontSize="12px" color="#8089A8">
              Latest
            </Typography>
            <Typography fontWeight={700} fontSize="18px" color="#2550d1">
              {latest}%
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography fontSize="12px" color="#8089A8">
              Peak
            </Typography>
            <Typography fontWeight={700} fontSize="18px" color="#10B981">
              {peak}%
            </Typography>
          </Box>
        </Box>
      </Box>

      <ResponsiveContainer width="100%" height={360}>
        <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 50 }}>
          <defs>
            <linearGradient id="confidenceFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#2550d1" stopOpacity={0.25} />
              <stop offset="95%" stopColor="#2550d1" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
          <XAxis
            dataKey="testStepResultId"
            angle={-45}
            textAnchor="end"
            height={70}
            tickLine={false}
            axisLine={{ stroke: "#e2e6f3" }}
            tick={{ fontSize: 11, fill: "#8089A8" }}
            label={{
              value: "Test Step Result ID",
              position: "insideBottom",
              offset: -5,
              fill: "#2B3539",
              fontSize: 13,
            }}
          />
          <YAxis
            domain={[0, 100]}
            width={60}
            tickLine={false}
            axisLine={{ stroke: "#e2e6f3" }}
            tick={{ fontSize: 12, fill: "#8089A8" }}
            label={{
              value: "Confidence Score (%)",
              angle: -90,
              position: "insideLeft",
              fill: "#2B3539",
              fontSize: 13,
              dy: 60,
            }}
          />
          <Tooltip
            cursor={{ stroke: "#2550d1", strokeWidth: 1, strokeDasharray: "4 4" }}
            contentStyle={{
              borderRadius: "8px",
              border: "1px solid #e2e6f3",
              boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
              fontSize: "13px",
            }}
            formatter={(value) => [`${value}%`, "Confidence Score"]}
            labelFormatter={(label) => `Result ID: ${label}`}
          />
          <Area
            type="monotone"
            dataKey="confidence_score"
            stroke="#2550d1"
            strokeWidth={2.5}
            fill="url(#confidenceFill)"
            dot={{ r: 4, fill: "#2550d1", strokeWidth: 2, stroke: "#fff" }}
            activeDot={{ r: 6, fill: "#2550d1", strokeWidth: 2, stroke: "#fff" }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default ConfidenceScoreGraph;